import { Alert, Button, Checkbox } from '@design-system';
import { Trans, useTranslation } from 'next-i18next';
import { useState } from 'react';

import {
  OrderReviewSummary,
  OrderReviewSummaryProps,
} from '../OrderReview/OrderReviewSummary';

export type ReviewLayoutProps = Pick<
  OrderReviewSummaryProps,
  | 'bid'
  | 'cardHold'
  | 'currencyCode'
  | 'framingSelections'
  | 'freeShipping'
  | 'isEstimate'
  | 'maxOrderValue'
  | 'maxQuantity'
  | 'maxQuantityNote'
  | 'onAddFrame'
  | 'orderValue'
  | 'orderedItems'
  | 'shippingCost'
  | 'showPreference'
  | 'showPreview'
  | 'taxRate'
  | 'taxValue'
> & {
  title: string;
  disclaimer?: string;
  error?: string;
  errorAction?: { label: string; onClick: () => void };
  submitLabel: string;
  resaleAgreementHref: string;
  isAuction?: boolean;
  isSubmitting?: boolean;
  onSubmit: () => void;
};

export const ReviewLayout = ({
  bid,
  cardHold,
  currencyCode,
  disclaimer,
  error,
  errorAction,
  framingSelections,
  freeShipping,
  isAuction = false,
  isEstimate,
  isSubmitting,
  maxOrderValue,
  maxQuantity,
  maxQuantityNote,
  onAddFrame,
  onSubmit,
  orderValue,
  orderedItems,
  resaleAgreementHref,
  shippingCost,
  showPreference,
  showPreview,
  submitLabel,
  taxRate,
  taxValue,
  title,
}: ReviewLayoutProps) => {
  const { t } = useTranslation('checkout');
  const [openSummaryIds, setOpenSummaryIds] = useState<Record<string, boolean>>(
    {},
  );
  const [termsAccepted, setTermsAccepted] = useState(false);

  const toggleSummary = (itemId: string) =>
    setOpenSummaryIds((prev) => ({ ...prev, [itemId]: !prev[itemId] }));

  return (
    <section className="flex flex-col gap-y-3">
      <h2 className="mb-9 text-center text-md font-bold lg:text-lg">{title}</h2>

      <OrderReviewSummary
        bid={bid}
        cardHold={cardHold}
        currencyCode={currencyCode}
        framingSelections={framingSelections}
        isEstimate={isEstimate}
        freeShipping={freeShipping}
        maxOrderValue={maxOrderValue}
        maxQuantity={maxQuantity}
        maxQuantityNote={maxQuantityNote}
        onAddFrame={onAddFrame}
        onToggleSummary={toggleSummary}
        openSummaryIds={openSummaryIds}
        orderValue={orderValue}
        orderedItems={orderedItems}
        shippingCost={shippingCost}
        showPreference={showPreference}
        showPreview={showPreview}
        taxRate={taxRate}
        taxValue={taxValue}
      />

      {error && (
        <div className="mt-9">
          <Alert status="error">
            {error}
            {errorAction && (
              <>
                {' '}
                <button
                  className="hyperlink"
                  onClick={errorAction.onClick}
                  type="button"
                >
                  {errorAction.label}
                </button>
              </>
            )}
          </Alert>
        </div>
      )}

      {isAuction && (
        <div className="mt-9">
          <Checkbox
            checked={termsAccepted}
            id="review-auction-terms"
            label={
              (
                <Trans
                  components={{
                    terms: (
                      <a
                        className="hyperlink"
                        href="https://avantarte.com/support/terms-and-conditions#auctions"
                        rel="noreferrer"
                        target="_blank"
                      />
                    ),
                  }}
                  i18nKey="review.auction.terms"
                  ns="checkout"
                  t={t}
                />
              ) as unknown as string
            }
            name="review-auction-terms"
            onChange={(event) => {
              setTermsAccepted(event.currentTarget.checked);
            }}
            type="checkbox"
          />
        </div>
      )}

      <div className={error || isAuction ? 'mt-3' : 'mt-9'}>
        <Button
          disabled={isAuction && !termsAccepted}
          fullWidth
          label={submitLabel}
          loading={isSubmitting}
          onClick={onSubmit}
          variant="primary"
        />
      </div>

      <p className="text-center text-xs text-neutral-4">
        {disclaimer && (
          <>
            {disclaimer}
            <br />
            <br />
          </>
        )}
        <Trans
          components={{
            faq: (
              <a
                className="hyperlink"
                href="https://avantarte.com/support/faq"
                rel="noreferrer"
                target="_blank"
              />
            ),
            resale: (
              <a
                className="hyperlink"
                href={resaleAgreementHref}
                rel="noreferrer"
                target="_blank"
              />
            ),
            terms: (
              <a
                className="hyperlink"
                href="https://avantarte.com/support/terms-and-conditions#draws"
                rel="noreferrer"
                target="_blank"
              />
            ),
          }}
          i18nKey={isAuction ? 'review.auction.legal' : 'review.legal'}
          ns="checkout"
          t={t}
        />
      </p>
    </section>
  );
};
